import { createRoute } from '@tanstack/react-router';
import { Authenticate, layoutRoute } from '@/router/root-routes.tsx';
import { usePageProps } from '@/hooks/use-page-props.ts';
import { AppForm } from '@/components/form/form.tsx';
import { z } from 'zod';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { d } from '@/lib/form.ts';
import { useAuth } from '@/stores/auth.store.ts';
import { InputField } from '@/components/form/input-field.tsx';
import { Field, FieldError, FieldLabel } from '@/components/ui/field.tsx';
import { PhoneInput } from '@/components/ui/phone-input.tsx';
import { config } from '@/config';
import { isValidPhoneNumber } from 'react-phone-number-input';
import { TUser } from '@/types/models.ts';
import { ComboboxField } from '@/components/form/combobox-field.tsx';
import { useGetTimezonesQuery, useSaveProfileMutation } from '@/app/account/api/queries.ts';
import { Button } from '@/components/ui/button.tsx';
import { useUpdateUserMutation } from '@/app/auth/api/queries.ts';
import { useEffect } from 'react';

export const profileRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: 'account/profile',
  component: ProfilePage,
  beforeLoad: Authenticate(),
});

const formSchema = z.object({
  first_name: z.string().min(1, { message: 'First Name is required' }),
  last_name: z.string().min(1, { message: 'Last Name is required' }),
  phone: z.string().refine(isValidPhoneNumber, { message: 'Phone number is invalid' }),
  timezone: z.string().optional(),
});

type FormType = z.input<typeof formSchema>;
function getPhoneNumber(user: TUser): string {
  const phone = user.phone as any;
  if (phone && isValidPhoneNumber(phone)) {
    return phone;
  }
  if (typeof phone === 'string') {
    const cleaned = phone.replace(/\D/g, '');
    if (user.site_id === 1) {
      if (cleaned.length === 10) {
        return '+1' + cleaned;
      }
    } else if (user.site_id === 2) {
      if (cleaned.length === 10) {
        return '+44' + cleaned;
      } else if (cleaned.length === 11 && cleaned.startsWith('0')) {
        return '+44' + cleaned.slice(1);
      }
    }
  }
  return String(phone);
}

function getDefaultValues(user: TUser) {
  return {
    first_name: d.str(user.name),
    last_name: d.str(user.surname),
    phone: d.str(getPhoneNumber(user)),
    timezone: d.str(user.timezone),
  };
}

function ProfilePage() {
  usePageProps({
    title: 'Profile',
  });

  const { user } = useAuth();
  const saveProfile = useSaveProfileMutation();
  const updateUserMutation = useUpdateUserMutation();

  const form = useForm<FormType>({
    resolver: zodResolver(formSchema),
    mode: 'onChange',
    defaultValues: getDefaultValues(user),
  });

  useEffect(() => {
    form.reset(getDefaultValues(user));
  }, [user]);

  function onSubmit(data: FormType) {
    saveProfile.mutateAsync(data).then(() => {
      updateUserMutation.mutate();
    });
    console.log('Form submitted:', data);
  }

  function onPhoneChange(value: string) {
    console.log('onPhoneChange:', value);
  }
  return (
    <div className="max-w-2xl mx-auto my-8">
      <h1 className="text-center mb-4">Your Account</h1>
      <AppForm form={form} onSubmit={onSubmit} className="w-full space-y-4 p-2 mx-auto mb-8">
        <div className="grid xl:grid-cols-3">
          <div className="flex flex-col gap-1">
            <FieldLabel>Email:</FieldLabel>
            <div>{user.email}</div>
          </div>
          <div className="flex flex-col gap-1">
            <FieldLabel>Company:</FieldLabel>
            <div>{user.company_name}</div>
          </div>
          <div className="flex flex-col gap-1">
            <FieldLabel>Rep:</FieldLabel>
            <div>{user.rep_name}</div>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 items-baseline">
          <InputField label="First Name" name="first_name" />
          <InputField label="Last Name" name="last_name" />
          <Controller
            control={form.control}
            name="phone"
            render={({ field, fieldState }) => (
              <Field className="flex flex-col items-start">
                <FieldLabel className="text-left">Phone Number</FieldLabel>
                <PhoneInput
                  placeholder=""
                  {...field}
                  defaultCountry={config.isUk() ? 'GB' : 'US'}
                  onChange={onPhoneChange}
                />
                {fieldState.invalid && <FieldError errors={[fieldState.error]} />}
              </Field>
            )}
          />
          <ComboboxField name="timezone" label="Timezone" useQuery={useGetTimezonesQuery} />
        </div>
        <div className="mt-8 flex justify-center">
          <Button disabled={!form.formState.isDirty}>Update</Button>
        </div>
      </AppForm>
    </div>
  );
}
